import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { KpiCard, RangeSelect, PageHeader, Panel, EmptyState } from "@/components/admin/shared";
import { TomorrowReminders } from "@/components/admin/TomorrowReminders";
import { MorningBriefing } from "@/components/admin/MorningBriefing";
import { StuckLeads } from "@/components/admin/StuckLeads";
import { CHART_COLORS, DASHBOARD_STAGES, STATUS_COLORS } from "@/lib/constants";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Legend, AreaChart, Area,
} from "recharts";

function Funnel({ data }) {
  const max = data[0]?.value || 1;
  return (
    <div className="space-y-2">
      {data.map((s, i) => (
        <div key={s.stage} data-testid={`funnel-stage-${i}`} className="flex items-center gap-3">
          <div className="w-28 text-xs font-semibold text-slate-500 text-right">{s.stage}</div>
          <div className="flex-1 h-9 bg-slate-100 rounded-lg overflow-hidden relative">
            <div className="h-full rounded-lg flex items-center px-3 text-white text-xs font-bold transition-all"
              style={{ width: `${Math.max((s.value / max) * 100, 6)}%`, background: CHART_COLORS[i % CHART_COLORS.length] }}>
              {s.value}
            </div>
          </div>
          <div className="w-12 text-xs font-mono-num font-bold text-slate-600">{s.pct}%</div>
        </div>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [range, setRange] = useState("today");
  const [kpis, setKpis] = useState(null);
  const [purpose, setPurpose] = useState([]);
  const [source, setSource] = useState([]);
  const [funnel, setFunnel] = useState([]);
  const [hourly, setHourly] = useState([]);
  const [live, setLive] = useState(null);
  const [stages, setStages] = useState([]);

  const load = async () => {
    const [k, p, s, f, h, l, st] = await Promise.all([
      api.get(`/dashboard/kpis?range=${range}`),
      api.get(`/analytics/purpose?range=${range}`),
      api.get(`/analytics/source?range=${range}`),
      api.get(`/analytics/funnel?range=${range}`),
      api.get(`/analytics/hourly?range=${range}`),
      api.get(`/dashboard/live`),
      api.get(`/dashboard/stages`),
    ]);
    setKpis(k.data); setPurpose(p.data); setSource(s.data); setFunnel(f.data); setHourly(h.data); setLive(l.data); setStages(st.data);
  };

  useEffect(() => { load(); }, [range]);
  useEffect(() => { const t = setInterval(load, 8000); return () => clearInterval(t); }, [range]);

  if (!kpis) return <div className="py-20 text-center text-slate-400">Loading dashboard…</div>;

  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Real-time visitor & conversion intelligence">
        <RangeSelect value={range} onChange={setRange} />
      </PageHeader>

      <Panel title="Lead Stages (right now)" className="mb-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          {DASHBOARD_STAGES.map((d) => (
            <button key={d.stage} onClick={() => navigate("/admin/leads")} data-testid={`dashboard-stage-${d.stage.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-left p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors">
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_COLORS[d.stage]}`}>{d.label}</span>
              <p className="font-display text-2xl font-extrabold text-[#081226] mt-2">{stages.find((x) => x.stage === d.stage)?.count ?? 0}</p>
            </button>
          ))}
        </div>
      </Panel>

      <MorningBriefing className="mb-4" />
      <StuckLeads className="mb-4" />
      <TomorrowReminders className="mb-4" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <KpiCard testid="dashboard-kpi-total-visitors" label="Total Visitors" value={kpis.total_visitors} sub={`${kpis.new_visitors} new · ${kpis.returning_visitors} returning`} accent="#081226" />
        <KpiCard testid="dashboard-kpi-enquiries" label="Enquiries" value={kpis.enquiries} accent="#0284C7" />
        <KpiCard testid="dashboard-kpi-trials" label="Meetings / Visits" value={kpis.trials} accent="#0284C7" />
        <KpiCard testid="dashboard-kpi-hot-leads" label="Pipeline" value={kpis.hot_leads} accent="#D95D1E" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard testid="dashboard-kpi-leads" label="Leads Generated" value={kpis.leads} accent="#8B5CF6" />
        <KpiCard testid="dashboard-kpi-conversions" label="Conversions" value={kpis.conversions} accent="#10B981" />
        <KpiCard testid="dashboard-kpi-conversion-rate" label="Conversion Rate" value={`${kpis.conversion_rate}%`} accent="#E8752A" />
        <KpiCard testid="dashboard-kpi-value" label="Conversion Value" value={`₹${(kpis.conversion_value/1000).toFixed(0)}k`} accent="#B45309" />
      </div>

      <div className="grid lg:grid-cols-3 gap-5 mb-5">
        <Panel title="Purpose of Visit" className="lg:col-span-1">
          {purpose.length === 0 ? <EmptyState text="No visits in range" /> : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={purpose} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
                  {purpose.map((e, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Panel>

        <Panel title="Visitors by Hour" className="lg:col-span-2">
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={hourly}>
              <defs><linearGradient id="gh" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#E8752A" stopOpacity={0.4} /><stop offset="100%" stopColor="#E8752A" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="hour" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip /><Area type="monotone" dataKey="visitors" stroke="#B8860B" strokeWidth={2} fill="url(#gh)" />
            </AreaChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <Panel title="Conversion Funnel" className="lg:col-span-2">
          <Funnel data={funnel} />
        </Panel>
        <Panel title="Live Feed" action={<span className="flex items-center gap-1.5 text-xs font-bold text-emerald-600"><span className="w-2 h-2 rounded-full bg-emerald-500 live-dot" />{live?.current_inside} inside</span>}>
          <div className="space-y-3 max-h-[300px] overflow-y-auto">
            {live?.feed?.slice(0, 8).map((f) => (
              <button key={f.visit_id} onClick={() => navigate(`/admin/visitors/${f.visitor_id}`)}
                className="w-full flex items-center gap-3 text-left hover:bg-slate-50 rounded-lg p-1.5">
                <div className="font-mono-num text-xs text-slate-400 w-16">{f.time_label}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#081226] truncate">{f.full_name}</p>
                  <p className="text-xs text-slate-400">{f.exit_reason === "Meeting done" ? `Meeting done · client outside ${f.exit_label}` : f.visiting_for}</p>
                </div>
                <span className="text-[10px] font-bold text-slate-500">{f.lead_stage || f.status}</span>
              </button>
            ))}
          </div>
        </Panel>
      </div>

      <div className="grid lg:grid-cols-1 gap-5 mt-5">
        <Panel title="Source Attribution">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={source}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="source" tick={{ fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} /><Tooltip /><Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="visitors" fill="#0f2042" radius={[4, 4, 0, 0]} />
              <Bar dataKey="leads" fill="#E8752A" radius={[4, 4, 0, 0]} />
              <Bar dataKey="conversions" fill="#10B981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </div>
    </div>
  );
}
